import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FullscreenHandler.css';

const FullscreenHandler = ({ children }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [allowGracePeriod, setAllowGracePeriod] = useState(false);
  const gracePeriodTimerRef = useRef(null);

  useEffect(() => {
    // Check if already in fullscreen
    const checkFullscreen = () => {
      const isCurrentlyFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setIsFullscreen(!!isCurrentlyFullscreen);

      // If user re-entered fullscreen, clear any pending grace period timer
      if (isCurrentlyFullscreen) {
        if (gracePeriodTimerRef.current) {
          clearTimeout(gracePeriodTimerRef.current);
          gracePeriodTimerRef.current = null;
        }
        setAllowGracePeriod(false);
        setShowPrompt(false);
        return;
      }

      // Only allow grace period if intimacy upload mode is active (set by Intimacy page or Admin page)
      const isUploadModeActive = window.__intimacyUploadMode === true;

      if (!isCurrentlyFullscreen && isUploadModeActive) {
        // Allow grace period indefinitely while upload mode is active
        // The grace period will end when upload mode is disabled (modal closes)
        // This prevents the fullscreen prompt from interfering with file picker dialogs
        setAllowGracePeriod(true);
        // Clear any existing timer
        if (gracePeriodTimerRef.current) {
          clearTimeout(gracePeriodTimerRef.current);
          gracePeriodTimerRef.current = null;
        }
      } else {
        // Immediately show prompt for all other cases
        setShowPrompt(!isCurrentlyFullscreen);
      }
    };

    checkFullscreen();

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      checkFullscreen();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      // Clean up timer on unmount
      if (gracePeriodTimerRef.current) {
        clearTimeout(gracePeriodTimerRef.current);
      }
    };
  }, []);

  const enterFullscreen = async () => {
    const elem = document.documentElement;

    try {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        await elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setShowPrompt(false);
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showPrompt && !allowGracePeriod && (
          <motion.div
            className="fullscreen-prompt"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="fullscreen-prompt-content parchment-texture">
              <motion.p
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                Please view in fullscreen
              </motion.p>
              <motion.button
                className="fullscreen-button"
                onClick={enterFullscreen}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Enter Fullscreen
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {(!showPrompt || allowGracePeriod) && children}
    </>
  );
};

export default FullscreenHandler;
